import { createApi } from '@reduxjs/toolkit/query/react'
import { sendNotification } from 'components'
import { Coordinate, MAP_SHAPES } from 'components/MapV2/types'
import { DynamicFieldRequestType } from 'containers/DynamicFormV2'
import { assetApi } from 'services/AssetManagement/asset'
import axiosBaseQuery from 'services/axiosBaseQuery'
import {
  ServicePointMapPopupDetailsRequest,
  ServicePointMapPopupDetailsResponse,
  SPMapSettingsResponse,
} from 'services/OpsManagement/types'
import { reportsApi } from 'services/ReportManagement/reports'
import {
  GenericResponse,
  MapPointsRequest,
  ServicePointMapPopupDetailResponse,
  TableRequestV2,
} from 'services/types'
import { sendExportNotification, sendImportNotification } from 'utils'
import i18n from 'utils/i18n'
import { entityApi } from './entity'
import { orderApi } from './order'
import {
  AddServicePointRequest,
  CustomFieldEngResponse,
  CustomFieldsRequest,
  EditServicePointRequest,
  ExportRequest,
  GetServicePointAssetsAndAssetTypesRequest,
  GetServicePointAssetsAndAssetTypesResponse,
  LinkAssetRequest,
  MutationResponse,
  SearchServicePointRequest,
  SearchServicePointResponse,
  ServicePointDetailRequest,
  ServicePointDetailResponse,
  ServicePointDetailTransformedResponse,
  ServicePointListRequest,
  ServicePointListResponse,
  ServicePointLocationRequest,
  ServicePointLocationsResponse,
  ServicePointLocationsTransformedResponse,
  ServicePointMappingRequest,
  ServicePointMappingResponse,
  TableResponseWithTimezone,
  UpdateStatusRequest,
} from './types'
import { servicePointApi as opsServicePointApi } from '../OpsManagement/servicePoints'

export const servicePointApi = createApi({
  reducerPath: 'engagementServicePointApi',
  tagTypes: [
    'ServicePoints',
    'ServicePointLocations',
    'ServicePointDetail',
    'ServicePointHistory',
    'ServicePointMapPoints',
    'ServicePointPopupDetails',
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/'}`,
  }),
  endpoints: (builder) => ({
    getServicePointList: builder.query<ServicePointListResponse, ServicePointListRequest>({
      query: ({ client_id, ...data }) => ({
        url: `engagement/clients/${client_id}/service_point/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['ServicePoints'],
    }),
    getServicePointLocations: builder.query<
      ServicePointLocationsTransformedResponse,
      ServicePointLocationRequest
    >({
      query: ({ client_id, ...data }) => ({
        url: `engagement/clients/${client_id}/service_point/location/list/`,
        method: 'POST',
        data,
      }),
      transformResponse: ({ data }: ServicePointLocationsResponse) => {
        return data.map(({ location: { address, coordinates, type }, ...rest }) => ({
          location: {
            coordinates: coordinates as Coordinate[],
            address: address as string,
            type: type.toLowerCase() as (typeof MAP_SHAPES)['single'],
          },
          ...rest,
        }))
      },
      providesTags: ['ServicePointLocations'],
    }),
    getServicePointDetails: builder.query<
      ServicePointDetailTransformedResponse,
      ServicePointDetailRequest
    >({
      query: ({ client_id, service_point_id }) => ({
        url: `engagement/clients/${client_id}/service_point/?service_point_id=${service_point_id}`,
        method: 'GET',
      }),
      transformResponse: ({ location, ...rest }: ServicePointDetailResponse) => {
        return {
          location: {
            coordinates: location.coordinates as Coordinate[],
            address: location.address as string,
            type: location.type.toLowerCase() as (typeof MAP_SHAPES)['single'],
          },
          ...rest,
        }
      },
      providesTags: ['ServicePointDetail'],
    }),
    getServicePointsCustomFields: builder.query<CustomFieldEngResponse, CustomFieldsRequest>({
      query: ({ clientId, type_id, ...data }) => ({
        url: `engagement/clients/${clientId}/service_point_dynamic_fields/`,
        method: 'GET',
        data,
        params: {
          service_point_type_id: type_id,
        },
      }),
    }),
    addServicePoint: builder.mutation<
      MutationResponse & { service_point_id: string },
      AddServicePointRequest
    >({
      query: ({ client_id, body }) => ({
        url: `engagement/clients/${client_id}/service_point/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: [
        'ServicePoints',
        'ServicePointDetail',
        'ServicePointHistory',
        'ServicePointMapPoints',
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
            hyperLink: {
              type: 'newTab',
              label: i18n.t('response.see_more'),
              to: `/engagement/service_points/detail/${data.service_point_id}`,
            },
          })
          dispatch(
            entityApi.util.invalidateTags([
              'Entities',
              'EntityDetail',
              'ListAllServicePointsOfEntity',
            ]),
          )
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    editServicePoint: builder.mutation<
      MutationResponse & { service_point_id: string },
      EditServicePointRequest
    >({
      query: ({ client_id, service_point_id, body }) => ({
        url: `engagement/clients/${client_id}/service_point/${service_point_id}/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: [
        'ServicePoints',
        'ServicePointDetail',
        'ServicePointHistory',
        'ServicePointPopupDetails',
        'ServicePointLocations',
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            entityApi.util.invalidateTags([
              'Entities',
              'EntityDetail',
              'ListAllServicePointsOfEntity',
            ]),
          )
          dispatch(
            orderApi.util.invalidateTags(['OrderPlanDetails', 'OrderPlans', 'OrderSettings']),
          )
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
            hyperLink: {
              type: 'newTab',
              label: i18n.t('response.see_more'),
              to: `/engagement/service_points/detail/${data.service_point_id}`,
            },
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    searchServicePoint: builder.mutation<SearchServicePointResponse, SearchServicePointRequest>({
      query: ({ client_id, ...data }) => ({
        url: `engagement/clients/${client_id}/service_point/search/`,
        method: 'POST',
        data,
      }),
    }),
    exportServicePoint: builder.mutation<
      MutationResponse,
      ExportRequest & { service_point_type_id?: string }
    >({
      query: ({ client_id, ...data }) => ({
        url: `engagement/clients/${client_id}/service_point/export/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (error) {}
      },
    }),
    linkAssetToServicePoint: builder.mutation<MutationResponse, LinkAssetRequest>({
      query: ({ client_id, servicePointId, ...data }) => ({
        url: `engagement/clients/${client_id}/service_point/${servicePointId}/link_asset/`,
        method: 'POST',
        data,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          dispatch(assetApi.util.invalidateTags(['Assets', 'AssetDetail', 'AssetMapPoints']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    unlinkAssetToServicePoint: builder.mutation<MutationResponse, LinkAssetRequest>({
      query: ({ client_id, servicePointId, ...data }) => ({
        url: `engagement/clients/${client_id}/service_point/${servicePointId}/unlink_asset/`,
        method: 'POST',
        data,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          dispatch(assetApi.util.invalidateTags(['Assets', 'AssetDetail', 'AssetMapPoints']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getServicePointMapping: builder.mutation<
      ServicePointMappingResponse,
      ServicePointMappingRequest
    >({
      query: ({ clientId, service_point_id }) => ({
        url: `engagement/external/clients/${clientId}/service_point_mapping/search/${service_point_id}/`,
        method: 'GET',
        errorOverride: true,
      }),
    }),
    importServicePoints: builder.mutation<MutationResponse, { clientId: string; body: FormData }>({
      query: ({ clientId, body }) => ({
        url: `engagement/clients/${clientId}/service_point/import/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['ServicePoints', 'ServicePointMapPoints'],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendImportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ImportReports']))
        } catch (error) {}
      },
    }),
    servicePointHistory: builder.query<
      TableResponseWithTimezone<{}>,
      TableRequestV2 & {
        service_point_id: string
      }
    >({
      query: ({ client_id, service_point_id, ...data }) => ({
        url: `engagement/clients/${client_id}/service_point/${service_point_id}/history/`,
        method: 'POST',
        data,
      }),
      providesTags: ['ServicePointHistory'],
    }),
    updateSpStatus: builder.mutation<MutationResponse, UpdateStatusRequest>({
      query: ({ client_id, ...data }) => ({
        url: `engagement/clients/${client_id}/status/update/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'ServicePoints',
        'ServicePointDetail',
        'ServicePointHistory',
        'ServicePointMapPoints',
      ],
    }),
    deleteServicePoint: builder.mutation<
      MutationResponse,
      {
        client_id: string
        service_point_id: string
        should_delete_linked_cases: boolean
        should_delete_linked_orders: boolean
      }
    >({
      query: ({ client_id, service_point_id, ...data }) => ({
        url: `engagement/clients/${client_id}/service_point/${service_point_id}/`,
        method: 'DELETE',
        data,
      }),
      invalidatesTags: ['ServicePoints', 'ServicePointMapPoints', 'ServicePointLocations'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          dispatch(
            entityApi.util.invalidateTags([
              'ListAllServicePointsOfEntity',
              'EntityDetail',
              'Entities',
            ]),
          )
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getMapPoints: builder.query<ServicePointMapPopupDetailResponse<string>, MapPointsRequest>({
      query: ({ clientId, ...data }) => ({
        url: `engagement/clients/${clientId}/service_point/get_service_points_map/`,
        method: 'POST',
        data,
      }),
      providesTags: ['ServicePointMapPoints'],
    }),
    getMapSettings: builder.query<SPMapSettingsResponse, { client_id: string }>({
      query: ({ client_id }) => ({
        url: `engagement/clients/${client_id}/service_point/get_service_points_map/settings/`,
        method: 'GET',
      }),
    }),
    getServicePointMapPopupDetails: builder.query<
      ServicePointMapPopupDetailsResponse,
      ServicePointMapPopupDetailsRequest
    >({
      query: ({ clientId, servicePointId, fields }) => ({
        url: `engagement/clients/${clientId}/service_point/get_service_points_map/detail/${servicePointId}/`,
        method: 'GET',
        params: {
          fields: fields ? fields.join(',') : '',
        },
      }),
      providesTags: ['ServicePointPopupDetails'],
    }),
    getServicePointAssetsAndAssetTypes: builder.query<
      GetServicePointAssetsAndAssetTypesResponse,
      GetServicePointAssetsAndAssetTypesRequest
    >({
      query: ({ client_id, service_point_id }) => ({
        url: `engagement/clients/${client_id}/service_point_asset/get_assets/`,
        method: 'GET',
        params: {
          service_point_id,
        },
      }),
    }),
    bulkDeleteServicePoint: builder.mutation<
      GenericResponse & {
        success_list: Array<{ id: string; name: string }>
        failure_list: Array<{
          id: string
          name: string
          type: 'not_found' | 'unknown'
        }>
      },
      {
        client_id: string
        id_list: string[]
        should_delete_linked_cases: boolean
        should_delete_linked_orders: boolean
      }
    >({
      query: ({ client_id, id_list, should_delete_linked_cases, should_delete_linked_orders }) => ({
        url: `engagement/clients/${client_id}/service_point/bulk_delete/`,
        method: 'POST',
        data: {
          id_list,
          should_delete_linked_cases,
          should_delete_linked_orders,
        },
      }),
      invalidatesTags: ['ServicePoints', 'ServicePointMapPoints', 'ServicePointLocations'],
    }),
    bulkChangeTypeServicePoint: builder.mutation<
      GenericResponse & {
        success_list: Array<{ id: string; name: string }>
        failure_list: Array<{
          id: string
          name: string
          type: 'not_found' | 'unknown'
        }>
      },
      {
        type_id: string
        id_list: Array<string>
        clientId: string
        dynamic_field_list: DynamicFieldRequestType
      }
    >({
      query: ({ clientId, ...data }) => ({
        url: `engagement/clients/${clientId}/service_point/bulk_edit/change_type/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'ServicePoints',
        'ServicePointMapPoints',
        'ServicePointLocations',
        'ServicePointDetail',
      ],
    }),
    linkOpsSp: builder.mutation<
      MutationResponse,
      {
        clientId: string
        servicePointId: string
        opsServicePointId: number
        opsServicePointName: string
      }
    >({
      query: ({ clientId, servicePointId, opsServicePointId, opsServicePointName }) => ({
        url: `engagement/clients/${clientId}/service_point/${servicePointId}/link_ops_service_point/`,
        method: 'PATCH',
        data: {
          ops_service_point_id: opsServicePointId,
          ops_service_point_name: opsServicePointName,
        },
      }),
      invalidatesTags: ['ServicePoints', 'ServicePointDetail'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          dispatch(opsServicePointApi.util.invalidateTags(['ServicePoints', 'ServicePointDetail']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
  }),
})

export const {
  useGetServicePointListQuery,
  useGetServicePointDetailsQuery,
  useGetServicePointsCustomFieldsQuery,
  useAddServicePointMutation,
  useEditServicePointMutation,
  useGetServicePointLocationsQuery,
  useLazyGetServicePointLocationsQuery,
  useSearchServicePointMutation,
  useExportServicePointMutation,
  useLinkAssetToServicePointMutation,
  useGetServicePointMappingMutation,
  useImportServicePointsMutation,
  useUnlinkAssetToServicePointMutation,
  useServicePointHistoryQuery,
  useUpdateSpStatusMutation,
  useDeleteServicePointMutation,
  useGetMapPointsQuery,
  useGetMapSettingsQuery,
  useGetServicePointMapPopupDetailsQuery,
  useGetServicePointAssetsAndAssetTypesQuery,
  useBulkDeleteServicePointMutation,
  useBulkChangeTypeServicePointMutation,
  useLinkOpsSpMutation,
  useLazyGetServicePointDetailsQuery,
} = servicePointApi
